import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { API } from "@/lib/api";
import { senhaForte } from "./CadastrarSenhaModal";
import {
  FONT_MAIN, STATUS_STYLE, glassCard, inputStyle,
  fmtCpf, EditField, SenhaField, AutorizacaoCard,
} from "./MobileFamiliares";
import { ArrowLeft, ChevronDown, Loader2, Pencil, Plus, ShieldAlert, Users, X } from "lucide-react";

const hojeISO = () => new Date().toISOString().slice(0, 10);

export default function MobilePrestadores({ origem, eql, cpf, onClose }) {
  const [itens, setItens] = useState(null);
  const [permissoes, setPermissoes] = useState(null);
  const [selecionado, setSelecionado] = useState(null);
  const [avisoAtivacao, setAvisoAtivacao] = useState(null);
  const [form, setForm] = useState(null);
  const [senhaForm, setSenhaForm] = useState({ nova: "", confirma: "" });
  const [showN, setShowN] = useState(false);
  const [showC, setShowC] = useState(false);
  const [erro, setErro] = useState("");
  const [salvando, setSalvando] = useState(false);
  const [onlyAtivas, setOnlyAtivas] = useState(true);
  const [expandedCpf, setExpandedCpf] = useState(null);
  const [novaAuth, setNovaAuth] = useState(null);
  const [revogarAuth, setRevogarAuth] = useState(null);
  const [processando, setProcessando] = useState(false);

  const qs = `origem=${encodeURIComponent(origem)}&eql=${encodeURIComponent(eql)}&cpf=${cpf}`;
  const carregar = useCallback(async () => {
    try {
      const r = await fetch(`${API}/public/prestadores?${qs}&only_ativas=${onlyAtivas}`);
      const d = await r.json();
      if (d.ok) setItens(d.itens);
      else toast.error("Não foi possível carregar os prestadores.");
      const rp = await fetch(`${API}/public/autorizacoes/permissoes?${qs}`);
      const dp = await rp.json();
      setPermissoes(dp.ok ? dp : { pode_autorizar_em: [] });
    } catch { toast.error("Falha de comunicação. Tente novamente."); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [origem, eql, cpf, onlyAtivas]);

  useEffect(() => { carregar(); }, [carregar]);

  const abrirEdicao = (p) => {
    setSelecionado(p);
    setForm({
      nome: p.nome || "", rg: p.rg || "", data_nascimento: String(p.data_nascimento || "").slice(0, 10),
      email: p.email || "", celular: `${p.ddi_celular || ""}${p.celular || ""}`.replace(/(?!^\+)\D/g, ""),
      autoriza_visitas: !!p.autoriza_visitas,
    });
    setSenhaForm({ nova: "", confirma: "" });
    setErro("");
  };

  const selecionar = (p) => {
    if (p.status_ativo !== "Ativo") setAvisoAtivacao(p);
    else abrirEdicao(p);
  };

  const salvar = async () => {
    setErro("");
    if (!form.nome.trim()) { setErro("O nome não pode ficar em branco."); return; }
    if (senhaForm.nova || senhaForm.confirma) {
      if (!senhaForte(senhaForm.nova)) {
        setErro("Senha fraca. Use ao menos 8 caracteres com letras e números, sem sequências.");
        return;
      }
      if (senhaForm.nova !== senhaForm.confirma) {
        setErro("A confirmação não confere com a nova senha.");
        return;
      }
    }
    setSalvando(true);
    try {
      const payload = {
        origem, eql, cpf, prestador_cpf: selecionado.cpf,
        nome: form.nome, rg: form.rg, data_nascimento: form.data_nascimento,
        email: form.email, celular: form.celular,
      };
      if (form.autoriza_visitas !== !!selecionado.autoriza_visitas) payload.autoriza_visitas = form.autoriza_visitas;
      if (senhaForm.nova) payload.senha = senhaForm.nova;
      const r = await fetch(`${API}/public/prestadores/atualizar`, {
        method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload),
      });
      const d = await r.json();
      if (d.ok) {
        toast.success(senhaForm.nova ? "Dados e senha atualizados." : "Dados atualizados.");
        setSelecionado(null);
        carregar();
      } else if (d.motivo === "senha_fraca") {
        setErro("Senha fraca. Use ao menos 8 caracteres com letras e números, sem sequências.");
      } else if (d.motivo === "prestador_nao_encontrado") {
        setErro("Prestador não localizado nesta unidade.");
      } else if (d.motivo === "dados_invalidos") {
        setErro("Dados inválidos. Verifique as informações preenchidas.");
      } else {
        setErro("Não foi possível salvar. Tente novamente.");
      }
    } catch { setErro("Falha de comunicação. Tente novamente."); }
    finally { setSalvando(false); }
  };

  const criarAutorizacao = async () => {
    if (!novaAuth?.eql_autorizada) return;
    setProcessando(true);
    try {
      const payload = {
        origem, eql, cpf, beneficiario_cpf: novaAuth.beneficiario.cpf,
        eql_autorizada: novaAuth.eql_autorizada,
      };
      if (novaAuth.validade_final) payload.validade_final = novaAuth.validade_final;
      const r = await fetch(`${API}/public/autorizacoes`, {
        method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload),
      });
      const d = await r.json();
      if (d.ok) {
        toast.success(d.renovado ? "Autorização renovada." : "Autorização criada.");
        setExpandedCpf(novaAuth.beneficiario.cpf);
        setNovaAuth(null);
        carregar();
      } else if (d.motivo === "sem_permissao") {
        toast.error("Você não tem permissão para autorizar nesta EQL.");
      } else if (d.motivo === "dados_invalidos") {
        toast.error("Data de validade inválida.");
      } else {
        toast.error("Não foi possível criar a autorização.");
      }
    } catch { toast.error("Falha de comunicação. Tente novamente."); }
    finally { setProcessando(false); }
  };

  const confirmarRevogacao = async () => {
    setProcessando(true);
    try {
      const url = `${API}/public/autorizacoes/${revogarAuth.autorizacao.id}?${qs}&beneficiario_cpf=${revogarAuth.beneficiario.cpf}`;
      const r = await fetch(url, { method: "DELETE" });
      const d = await r.json();
      if (d.ok) {
        toast.success("Autorização revogada.");
        setRevogarAuth(null);
        carregar();
      } else if (d.motivo === "somente_autorizador") {
        toast.error("Somente quem criou a autorização pode revogá-la.");
      } else {
        toast.error("Não foi possível revogar a autorização.");
      }
    } catch { toast.error("Falha de comunicação. Tente novamente."); }
    finally { setProcessando(false); }
  };

  const podeAutorizar = (permissoes?.pode_autorizar_em || []).length > 0;

  return (
    <div className="fixed inset-0 z-40 flex flex-col" style={{ background: "#11131b", fontFamily: FONT_MAIN }}
      data-testid="appmobile-prestadores-overlay">
      <header className="flex items-center gap-3 px-4 py-3.5"
        style={{ borderBottom: "1px solid rgba(255,255,255,0.06)", paddingTop: "max(14px, env(safe-area-inset-top))" }}>
        <button type="button" data-testid="appmobile-prestadores-voltar" aria-label="Voltar"
          onClick={() => (selecionado ? setSelecionado(null) : onClose())}
          className="w-10 h-10 flex items-center justify-center rounded-xl active:bg-white/10"
          style={{ color: "#c3c6d7", touchAction: "manipulation" }}>
          <ArrowLeft className="h-5 w-5" />
        </button>
        <h1 className="flex-1 text-base font-bold" style={{ color: "#b4c5ff" }} data-testid="appmobile-prestadores-titulo">
          {selecionado ? "Alterar Prestador" : "Prestadores"}
        </h1>
        {!selecionado && (
          <button type="button" data-testid="appmobile-prestadores-incluir" aria-label="Incluir prestador"
            className="inline-flex items-center gap-1 rounded-xl px-3 h-9 text-xs font-semibold"
            style={{ background: "rgba(37,99,235,0.2)", color: "#8ab4ff", border: "1px solid rgba(138,180,255,0.3)", touchAction: "manipulation" }}>
            <Plus className="h-4 w-4" /> Incluir
          </button>
        )}
      </header>

      <main className="flex-1 overflow-y-auto px-4 py-4 pb-28">
        {!selecionado ? (
          !itens ? (
            <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin" style={{ color: "#8d90a0" }} /></div>
          ) : itens.length === 0 ? (
            <div className="flex flex-col items-center gap-2 py-16" data-testid="appmobile-prestadores-vazio">
              <Users className="h-8 w-8" style={{ color: "#8d90a0" }} />
              <p className="text-center text-xs" style={{ color: "#8d90a0" }}>Nenhum prestador ou familiar cadastrado nesta unidade.</p>
            </div>
          ) : (
            <>
              <label className="mb-3 flex items-center gap-2.5 rounded-xl px-4 py-3 cursor-pointer"
                style={{ ...glassCard, color: "#c3c6d7" }} data-testid="appmobile-prestadores-toggle-label">
                <input type="checkbox" data-testid="appmobile-prestadores-toggle-todas" checked={!onlyAtivas}
                  onChange={(e) => { setOnlyAtivas(!e.target.checked); setItens(null); setExpandedCpf(null); }}
                  className="w-4 h-4 accent-[#2563EB]" />
                <span className="text-xs font-medium">Listar ativas e inativas</span>
              </label>
              <div className="rounded-xl overflow-hidden" style={glassCard} data-testid="appmobile-prestadores-lista">
                <div className="grid grid-cols-[minmax(0,1fr)_64px_58px_72px] gap-2 px-3 py-2.5"
                  style={{ borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
                  <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: "#8d90a0" }}>Nome</span>
                  <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: "#8d90a0" }}>Tipo</span>
                  <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: "#8d90a0" }}>Ativo</span>
                  <span />
                </div>
                {itens.map((p) => {
                  const st = STATUS_STYLE[p.status_ativo] || STATUS_STYLE.Ativo;
                  const expanded = expandedCpf === p.cpf;
                  const autorizacoes = p.eqls_autorizadas || [];
                  return (
                    <div key={p.id || p.cpf} data-testid={`appmobile-prestador-item-${p.cpf}`}
                      style={{ borderBottom: "1px solid rgba(255,255,255,0.05)" }}>
                      <div className="grid grid-cols-[minmax(0,1fr)_64px_58px_72px] gap-2 items-center px-3 py-3">
                        <span className="min-w-0 truncate text-xs font-semibold" style={{ color: "#e1e2ed" }}
                          data-testid={`appmobile-prestador-nome-${p.cpf}`}>{p.nome || "—"}</span>
                        <span className="text-[10px] text-center rounded-full px-1.5 py-1"
                          style={{ background: "rgba(180,197,255,0.08)", color: "#b4c5ff", border: "1px solid rgba(180,197,255,0.22)" }}
                          data-testid={`appmobile-prestador-tipo-${p.cpf}`}>{p.tipo_label}</span>
                        <span className="text-[10px] font-bold text-center rounded-full px-1.5 py-1"
                          style={{ background: st.bg, color: st.color, border: `1px solid ${st.border}` }}
                          data-testid={`appmobile-prestador-ativo-${p.cpf}`}>{p.status_ativo}</span>
                        <div className="flex items-center justify-end gap-0.5">
                          <button type="button" data-testid={`appmobile-prestador-edit-${p.cpf}`} aria-label={`Editar ${p.nome || "prestador"}`}
                            onClick={() => selecionar(p)}
                            className="w-9 h-9 flex items-center justify-center rounded-lg active:bg-white/10">
                            <Pencil className="h-4 w-4" style={{ color: "#8ab4ff" }} />
                          </button>
                          <button type="button" data-testid={`appmobile-prestador-toggle-${p.cpf}`}
                            aria-label={`${expanded ? "Recolher" : "Expandir"} autorizações de ${p.nome || "prestador"}`}
                            aria-expanded={expanded}
                            onClick={() => setExpandedCpf(expanded ? null : p.cpf)}
                            className="w-9 h-9 flex items-center justify-center rounded-lg active:bg-white/10">
                            <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${expanded ? "rotate-180" : ""}`}
                              style={{ color: "#8d90a0" }} />
                          </button>
                        </div>
                      </div>
                      {expanded && (
                        <div className="px-3 pb-3 pt-1" data-testid={`appmobile-prestador-autorizacoes-${p.cpf}`}>
                          <div className="flex items-center justify-between gap-2 mb-2">
                            <p className="text-[10px] font-bold uppercase tracking-wider" style={{ color: "#b4c5ff" }}>
                              Autorizações
                            </p>
                            {podeAutorizar && (
                              <button type="button" data-testid={`appmobile-prestador-nova-autorizacao-${p.cpf}`}
                                onClick={() => setNovaAuth({
                                  beneficiario: p,
                                  eql_autorizada: permissoes.pode_autorizar_em[0]?.eql || "",
                                  validade_final: "",
                                })}
                                className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[10px] font-bold active:scale-95 transition-transform"
                                style={{ background: "rgba(37,99,235,0.15)", color: "#8ab4ff", border: "1px solid rgba(138,180,255,0.3)" }}>
                                <Plus className="h-3 w-3" /> Adicionar
                              </button>
                            )}
                          </div>
                          {autorizacoes.length ? (
                            <div className="flex flex-col gap-2">
                              {autorizacoes.map((a) => (
                                <AutorizacaoCard key={a.id || a.eql} a={a}
                                  testid={`appmobile-prestador-autorizacao-${p.cpf}-${a.id || a.eql}`}
                                  podeRevogar={a.autorizador_id === permissoes?.autorizador_id && a.ativo !== false}
                                  onRevogar={() => setRevogarAuth({ beneficiario: p, autorizacao: a })} />
                              ))}
                            </div>
                          ) : (
                            <p className="text-[11px]" style={{ color: "#8d90a0" }}
                              data-testid={`appmobile-prestador-autorizacoes-vazio-${p.cpf}`}>
                              {onlyAtivas ? "Nenhuma autorização ativa." : "Nenhuma autorização."}
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )
        ) : (
          <div className="flex flex-col gap-4" data-testid="appmobile-prestador-form">
            <div className="text-center mb-1">
              <p className="text-sm font-semibold" style={{ color: "#e1e2ed" }} data-testid="appmobile-prestador-local">
                {selecionado.local_acesso_descricao || "—"}
              </p>
              <p className="text-xs mt-0.5" style={{ color: "#8d90a0" }} data-testid="appmobile-prestador-identidade">
                {selecionado.eql} · {selecionado.tipo_label} · Matrícula {selecionado.matricula || "—"} · CPF {fmtCpf(selecionado.cpf)}
              </p>
            </div>
            <EditField label="Nome" testid="appmobile-prestador-edit-nome" value={form.nome}
              onChange={(e) => setForm({ ...form, nome: e.target.value })} placeholder="Nome completo" />
            <EditField label="RG" testid="appmobile-prestador-edit-rg" value={form.rg}
              onChange={(e) => setForm({ ...form, rg: e.target.value })} placeholder="Somente números" inputMode="numeric" />
            <EditField label="Nascimento" testid="appmobile-prestador-edit-nascimento" type="date" value={form.data_nascimento}
              onChange={(e) => setForm({ ...form, data_nascimento: e.target.value })} />
            <EditField label="E-mail" testid="appmobile-prestador-edit-email" type="email" value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="email@dominio.com" />
            <EditField label="Whatsapp" testid="appmobile-prestador-edit-whatsapp" value={form.celular}
              onChange={(e) => setForm({ ...form, celular: e.target.value })} placeholder="+5511999999999" inputMode="tel" />

            <label className="flex items-start gap-2.5 text-xs rounded-xl px-4 py-3 cursor-pointer"
              style={{ ...glassCard, color: "#c3c6d7" }} data-testid="appmobile-prestador-autoriza-label">
              <input type="checkbox" data-testid="appmobile-prestador-autoriza" checked={form.autoriza_visitas}
                onChange={(e) => setForm({ ...form, autoriza_visitas: e.target.checked })}
                className="mt-0.5 w-4 h-4 accent-[#2563EB]" />
              <span>
                <span className="block font-semibold" style={{ color: "#e1e2ed" }}>Autoriza visitas</span>
                <span className="block text-[10px] mt-0.5" style={{ color: "#8d90a0" }}>
                  Define se o prestador pode receber visitas cadastradas pelo porteiro.
                </span>
              </span>
            </label>

            <div className="rounded-xl p-4 relative overflow-hidden" style={glassCard} data-testid="appmobile-prestador-senha-secao">
              <p className="text-[11px] font-semibold uppercase tracking-wider mb-1" style={{ color: "#b4c5ff" }}>Senha</p>
              <p className="text-[11px] mb-3" style={{ color: "#8d90a0" }}>
                Mantenha em branco para conservar a senha atual. Para alterar, preencha e confirme nos dois campos.
              </p>
              <div className="flex flex-col gap-3">
                <SenhaField label="Nova senha" value={senhaForm.nova} onChange={(x) => setSenhaForm({ ...senhaForm, nova: x })}
                  show={showN} toggle={() => setShowN(!showN)} testid="appmobile-prestador-edit-senha" />
                <SenhaField label="Confirmar nova senha" value={senhaForm.confirma} onChange={(x) => setSenhaForm({ ...senhaForm, confirma: x })}
                  show={showC} toggle={() => setShowC(!showC)} testid="appmobile-prestador-edit-senha2" />
              </div>
            </div>

            {erro && (
              <div data-testid="appmobile-prestador-erro"
                className="flex items-start gap-2 text-xs rounded-xl px-3 py-2.5"
                style={{ background: "rgba(255,180,171,0.08)", color: "#ffb4ab", border: "1px solid rgba(255,180,171,0.3)" }}>
                <ShieldAlert className="h-4 w-4 shrink-0 mt-px" />
                <span>{erro}</span>
              </div>
            )}

            <div className="flex gap-3">
              <button type="button" data-testid="appmobile-prestador-cancelar" onClick={() => setSelecionado(null)} disabled={salvando}
                className="flex-1 py-3 rounded-xl text-sm font-medium active:scale-[0.98] transition-transform"
                style={{ color: "#8d90a0", background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)" }}>
                Cancelar
              </button>
              <button type="button" data-testid="appmobile-prestador-salvar" onClick={salvar} disabled={salvando}
                className="flex-1 py-3 rounded-xl text-sm font-semibold text-white active:scale-[0.98] transition-all disabled:opacity-40"
                style={{ background: "#2563EB", boxShadow: "0 0 15px rgba(37,99,235,0.3)" }}>
                {salvando ? <Loader2 className="h-5 w-5 animate-spin mx-auto" /> : "Salvar"}
              </button>
            </div>
          </div>
        )}
      </main>

      {avisoAtivacao && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-6" data-testid="appmobile-prestador-ativacao-modal">
          <div className="absolute inset-0" style={{ background: "rgba(0,0,0,0.72)" }} />
          <div className="relative w-full max-w-sm rounded-[14px] overflow-hidden p-5" style={glassCard}>
            <button type="button" data-testid="appmobile-prestador-ativacao-fechar" aria-label="Fechar"
              onClick={() => setAvisoAtivacao(null)}
              className="absolute right-3 top-3 w-9 h-9 flex items-center justify-center rounded-xl active:bg-white/10"
              style={{ color: "#8d90a0" }}>
              <X className="h-4 w-4" />
            </button>
            <ShieldAlert className="h-8 w-8 mx-auto mb-3" style={{ color: "#f4a261" }} />
            <h2 className="text-base font-bold text-center" style={{ color: "#e1e2ed" }}
              data-testid="appmobile-prestador-ativacao-titulo">Cadastro {avisoAtivacao.status_ativo}</h2>
            <p className="text-xs text-center mt-2 mb-5" style={{ color: "#c3c6d7" }}
              data-testid="appmobile-prestador-ativacao-texto">
              Solicite a ativação deste cadastro à administração. Você ainda pode alterar os dados agora.
            </p>
            <div className="flex gap-3">
              <button type="button" data-testid="appmobile-prestador-ativacao-voltar" onClick={() => setAvisoAtivacao(null)}
                className="flex-1 py-3 rounded-xl text-sm font-medium"
                style={{ color: "#8d90a0", background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)" }}>
                Voltar
              </button>
              <button type="button" data-testid="appmobile-prestador-ativacao-continuar"
                onClick={() => { abrirEdicao(avisoAtivacao); setAvisoAtivacao(null); }}
                className="flex-1 py-3 rounded-xl text-sm font-semibold text-white"
                style={{ background: "#2563EB" }}>
                Continuar
              </button>
            </div>
          </div>
        </div>
      )}

      {novaAuth && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-6" data-testid="appmobile-autorizacao-modal">
          <div className="absolute inset-0" style={{ background: "rgba(0,0,0,0.72)" }} />
          <div className="relative w-full max-w-sm rounded-[14px] overflow-hidden p-5" style={glassCard}>
            <h2 className="text-base font-bold text-center" style={{ color: "#e1e2ed" }}
              data-testid="appmobile-autorizacao-titulo">Nova autorização</h2>
            <p className="text-xs text-center mt-1 mb-4" style={{ color: "#8d90a0" }}
              data-testid="appmobile-autorizacao-beneficiario">{novaAuth.beneficiario.nome}</p>
            <div className="flex flex-col gap-3">
              <div>
                <label className="block text-[11px] font-semibold uppercase tracking-wider mb-1" style={{ color: "#c3c6d7" }}>EQL autorizada</label>
                <select data-testid="appmobile-autorizacao-eql" value={novaAuth.eql_autorizada}
                  onChange={(e) => setNovaAuth({ ...novaAuth, eql_autorizada: e.target.value })}
                  className="w-full rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[#b4c5ff]"
                  style={inputStyle}>
                  {permissoes.pode_autorizar_em.map((p) => (
                    <option key={p.eql} value={p.eql} label={`${p.eql} — ${p.motivo}`} />
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-[11px] font-semibold uppercase tracking-wider mb-1" style={{ color: "#c3c6d7" }}>Validade final</label>
                <input type="date" min={hojeISO()} data-testid="appmobile-autorizacao-validade" value={novaAuth.validade_final}
                  onChange={(e) => setNovaAuth({ ...novaAuth, validade_final: e.target.value })}
                  className="w-full rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[#b4c5ff]"
                  style={inputStyle} />
                <p className="text-[10px] mt-1" style={{ color: "#8d90a0" }} data-testid="appmobile-autorizacao-validade-info">
                  Em branco será 1 ano.
                </p>
              </div>
            </div>
            <div className="flex gap-3 mt-5">
              <button type="button" data-testid="appmobile-autorizacao-cancelar" onClick={() => setNovaAuth(null)} disabled={processando}
                className="flex-1 py-3 rounded-xl text-sm font-medium"
                style={{ color: "#8d90a0", background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)" }}>
                Voltar
              </button>
              <button type="button" data-testid="appmobile-autorizacao-confirmar" onClick={criarAutorizacao} disabled={processando}
                className="flex-1 py-3 rounded-xl text-sm font-semibold text-white disabled:opacity-40"
                style={{ background: "#2563EB" }}>
                {processando ? <Loader2 className="h-5 w-5 animate-spin mx-auto" /> : "Confirmar"}
              </button>
            </div>
          </div>
        </div>
      )}

      {revogarAuth && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-6" data-testid="appmobile-revogar-modal">
          <div className="absolute inset-0" style={{ background: "rgba(0,0,0,0.72)" }} />
          <div className="relative w-full max-w-sm rounded-[14px] overflow-hidden p-5" style={glassCard}>
            <ShieldAlert className="h-8 w-8 mx-auto mb-3" style={{ color: "#ffb4ab" }} />
            <h2 className="text-base font-bold text-center" style={{ color: "#e1e2ed" }}
              data-testid="appmobile-revogar-titulo">Revogar autorização</h2>
            <p className="text-xs text-center mt-2 mb-5" style={{ color: "#c3c6d7" }}
              data-testid="appmobile-revogar-texto">
              Revogar a autorização de {revogarAuth.beneficiario.nome} em {revogarAuth.autorizacao.eql}?
            </p>
            <div className="flex gap-3">
              <button type="button" data-testid="appmobile-revogar-cancelar" onClick={() => setRevogarAuth(null)} disabled={processando}
                className="flex-1 py-3 rounded-xl text-sm font-medium"
                style={{ color: "#8d90a0", background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)" }}>
                Voltar
              </button>
              <button type="button" data-testid="appmobile-revogar-confirmar" onClick={confirmarRevogacao} disabled={processando}
                className="flex-1 py-3 rounded-xl text-sm font-semibold text-white disabled:opacity-40"
                style={{ background: "#dc2626" }}>
                {processando ? <Loader2 className="h-5 w-5 animate-spin mx-auto" /> : "Revogar"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
